import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText
} from '@material-ui/core'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { PricingUtilitiesError } from '../common/errorMessages'
import PricingModelCrudUtilities from '../common/PricingModelCrudUtilities'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  modelCard: {
    marginBottom: theme.spacing(2),
  },
  priceSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
  },
  materialSection: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
  },
  loadsContainer: {
    marginTop: theme.spacing(1),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
  },
}))

PricingModelsTable.propTypes = {
  firebase: PropTypes.object.isRequired,
  db: PropTypes.object.isRequired,
}

export default function PricingModelsTable(props) {
  const classes = useStyles()
  const [pricingModels, setPricingModels] = useState([])
  const [editingModel, setEditingModel] = useState(null)
  const [newModelDialog, setNewModelDialog] = useState(false)
  const [newModelName, setNewModelName] = useState('')
  const [errors, setErrors] = useState({})
  const [pricingModelCrudUtilities] = useState(new PricingModelCrudUtilities(props.firebase, props.db))

  useEffect(() => {
    const unsubscribe = props.db.collection('pricing')
      .onSnapshot({
        includeMetadataChanges: true
      }, querySnapshot => {
        // Only update state when we have server data, not just cached data
        if (!querySnapshot.metadata.fromCache) {
          const models = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ref: doc.ref,
            ...doc.data()
          }))
          setPricingModels(models)
          
          // Clear editing state if the currently edited model no longer exists
          if (editingModel && !models.find(m => m.name === editingModel.name)) {
            setEditingModel(null)
            setErrors({})
          }
        }
      })
    
    return () => {
      unsubscribe()
    }
  }, [props.db, editingModel])

  const formatDateForStorage = (date = new Date()) => {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}/${day}/${year}`
  }

  const validateLoads = (loads) => {
    if (loads.length === 2) {
      const [singleLoad, doubleLoadPerItem] = loads
      if (doubleLoadPerItem >= singleLoad) {
        return 'Second load price must be less than first load price'
      }
    }
    return null
  }

  const handleEditModel = (model) => {
    // Always get fresh data from the models array to avoid stale state
    const freshModel = pricingModels.find(m => m.name === model.name)
    if (freshModel) {
      setEditingModel(JSON.parse(JSON.stringify(freshModel))) // Deep clone from fresh data
    } else {
      setEditingModel(JSON.parse(JSON.stringify(model))) // Fallback to passed model
    }
    setErrors({})
  }

  const handleSaveModel = async () => {
    // Validate all loads
    const validationErrors = {}
    let hasErrors = false

    editingModel.prices.forEach((price, priceIndex) => {
      price.materials.forEach((material, materialIndex) => {
        const loadError = validateLoads(material.loads)
        if (loadError) {
          validationErrors[`${priceIndex}-${materialIndex}`] = loadError
          hasErrors = true
        }
      })
    })

    if (hasErrors) {
      setErrors(validationErrors)
      return
    }

    try {
      const { id, ref, ...modelData } = editingModel
      await pricingModelCrudUtilities.updatePricingModelByName(editingModel.name, modelData)
      setEditingModel(null)
      setErrors({})
    } catch (error) {
      alert(`Error updating pricing model: ${error.message}`)
    }
  }

  const handleDeletePricingModel = async (modelName) => {
    // Check if it's the default model
    if (modelName.toLowerCase() === 'default') {
      alert(PricingUtilitiesError.cannotDeleteDefaultModel)
      return
    }

    // Check if any customers are using this pricing model
    try {
      const customersSnapshot = await props.db.collection('customers')
        .where('pricingModel', '==', modelName)
        .get()
      
      if (!customersSnapshot.empty) {
        alert(PricingUtilitiesError.cannotDeleteModelInUse)
        return
      }

      // Proceed with deletion if validation passes
      if (window.confirm(`Are you sure you want to delete the pricing model "${modelName}"?`)) {
        // Clear editing state if we're deleting the currently edited model
        if (editingModel && editingModel.name === modelName) {
          setEditingModel(null)
          setErrors({})
        }
        
        await pricingModelCrudUtilities.deletePricingModelByName(modelName)
        
        // Force a fresh fetch from server after deletion to clear any cached data
        const freshSnapshot = await props.db.collection('pricing').get({ source: 'server' })
        const models = freshSnapshot.docs.map(doc => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data()
        }))
        setPricingModels(models)
      }
    } catch (error) {
      alert(`Error deleting pricing model: ${error.message}`)
    }
  }

  const handleCancelEdit = () => {
    setEditingModel(null)
    setErrors({})
  }

  const handleAddNewModel = async () => {
    if (!newModelName.trim()) {
      alert(PricingUtilitiesError.pricingModelMustHaveName)
      return
    }

    const newModel = {
      name: newModelName,
      prices: [{
        effectiveDate: formatDateForStorage(),
        city: '',
        materials: [{
          material: ['c/s'],
          loads: [250]
        }]
      }]
    }

    try {
      await pricingModelCrudUtilities.addPricingModel(newModel)
      setNewModelDialog(false)
      setNewModelName('')
      
      // Force a fresh fetch from server after creation to ensure we get the new data
      const freshSnapshot = await props.db.collection('pricing').get({ source: 'server' })
      const models = freshSnapshot.docs.map(doc => ({
        id: doc.id,
        ref: doc.ref,
        ...doc.data()
      }))
      setPricingModels(models)
    } catch (error) {
      alert(`Error adding pricing model: ${error.message}`)
    }
  }

  const updateModelField = (path, value) => {
    const updatedModel = JSON.parse(JSON.stringify(editingModel))
    const pathArray = path.split('.')
    let current = updatedModel

    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]]
    }
    current[pathArray[pathArray.length - 1]] = value
    setEditingModel(updatedModel)
  }

  const addPrice = () => {
    const updatedModel = JSON.parse(JSON.stringify(editingModel))
    updatedModel.prices.push({
      effectiveDate: formatDateForStorage(),
      city: '',
      materials: [{
        material: ['c/s'],
        loads: [250]
      }]
    })
    setEditingModel(updatedModel)
  }

  const removePrice = (priceIndex) => {
    const price = editingModel.prices[priceIndex]
    const cityLabel = price.city ? price.city : 'all cities'
    if (window.confirm(`Are you sure you want to remove the price entry for effective date ${price.effectiveDate}, ${cityLabel}?`)) {
      const updatedModel = JSON.parse(JSON.stringify(editingModel))
      updatedModel.prices.splice(priceIndex, 1)
      setEditingModel(updatedModel)
    }
  }

  const addMaterial = (priceIndex) => {
    const updatedModel = JSON.parse(JSON.stringify(editingModel))
    updatedModel.prices[priceIndex].materials.push({
      material: ['c/s'],
      loads: [250]
    })
    setEditingModel(updatedModel)
  }

  const removeMaterial = (priceIndex, materialIndex) => {
    const materialNames = editingModel.prices[priceIndex].materials[materialIndex].material.join(', ')
    if (window.confirm(`Are you sure you want to remove the material group "${materialNames}"?`)) {
      const updatedModel = JSON.parse(JSON.stringify(editingModel))
      updatedModel.prices[priceIndex].materials.splice(materialIndex, 1)
      setEditingModel(updatedModel)
    }
  }

  const updateMaterialNames = (priceIndex, materialIndex, value) => {
    const updatedModel = JSON.parse(JSON.stringify(editingModel))
    updatedModel.prices[priceIndex].materials[materialIndex].material = value.split(',').map(s => s.trim())
    setEditingModel(updatedModel)
  }

  const updateLoads = (priceIndex, materialIndex, loadIndex, value) => {
    const updatedModel = JSON.parse(JSON.stringify(editingModel))
    const numericValue = parseFloat(value) || 0
    updatedModel.prices[priceIndex].materials[materialIndex].loads[loadIndex] = numericValue
    
    // Clear any existing error for this material
    const errorKey = `${priceIndex}-${materialIndex}`
    if (errors[errorKey]) {
      const newErrors = { ...errors }
      delete newErrors[errorKey]
      setErrors(newErrors)
    }
    
    setEditingModel(updatedModel)
  }

  const addSecondLoad = (priceIndex, materialIndex) => {
    const updatedModel = JSON.parse(JSON.stringify(editingModel))
    updatedModel.prices[priceIndex].materials[materialIndex].loads.push(0)
    setEditingModel(updatedModel)
  }

  const removeSecondLoad = (priceIndex, materialIndex) => {
    const updatedModel = JSON.parse(JSON.stringify(editingModel))
    updatedModel.prices[priceIndex].materials[materialIndex].loads.pop()
    setEditingModel(updatedModel)
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Pricing Models
      </Typography>

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => setNewModelDialog(true)}
        style={{ marginBottom: '16px' }}
      >
        Add New Pricing Model
      </Button>

      {[...pricingModels]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((model, modelIndex) => (
        <Card key={model.id} className={classes.modelCard}>
          <CardContent>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="h5">{model.name}</Typography>
              <div>
                <IconButton onClick={() => handleEditModel(model)}>
                  <EditIcon />
                </IconButton>
                <IconButton 
                  onClick={() => handleDeletePricingModel(model.name)}
                  color="secondary"
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            </Grid>

            {editingModel && editingModel.name === model.name ? (
              <div>
                <TextField
                  fullWidth
                  label="Model Name"
                  value={editingModel.name}
                  onChange={(e) => updateModelField('name', e.target.value)}
                  margin="normal"
                />

                {editingModel.prices.map((price, priceIndex) => (
                  <Paper key={priceIndex} className={classes.priceSection}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Effective Date"
                          value={price.effectiveDate}
                          onChange={(e) => updateModelField(`prices.${priceIndex}.effectiveDate`, e.target.value)}
                          helperText="MM/DD/YYYY format"
                          placeholder="03/29/2026"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="City (leave blank for all)"
                          value={price.city}
                          onChange={(e) => updateModelField(`prices.${priceIndex}.city`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          variant={editingModel.prices.length === 1 ? "outlined" : "contained"}
                          color="secondary"
                          onClick={() => removePrice(priceIndex)}
                          disabled={editingModel.prices.length === 1}
                        >
                          Remove Price
                        </Button>
                      </Grid>
                    </Grid>

                    <Typography variant="h6" style={{ marginTop: '16px' }}>
                      Materials
                    </Typography>

                    {price.materials.map((material, materialIndex) => (
                      <Paper key={materialIndex} className={classes.materialSection}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Materials (comma-separated)"
                              value={material.material.join(', ')}
                              onChange={(e) => updateMaterialNames(priceIndex, materialIndex, e.target.value)}
                              helperText="Enter material names separated by commas"
                            />
                          </Grid>

                          <Grid item xs={5}>
                            <TextField
                              fullWidth
                              label="Single Load Price"
                              type="number"
                              value={material.loads[0] || 0}
                              onChange={(e) => updateLoads(priceIndex, materialIndex, 0, e.target.value)}
                            />
                          </Grid>

                          {material.loads.length > 1 ? (
                            <>
                              <Grid item xs={5}>
                                <TextField
                                  fullWidth
                                  label="Two Load Price (per load)"
                                  type="number"
                                  value={material.loads[1] || 0}
                                  onChange={(e) => updateLoads(priceIndex, materialIndex, 1, e.target.value)}
                                  error={!!errors[`${priceIndex}-${materialIndex}`]}
                                />
                                {errors[`${priceIndex}-${materialIndex}`] && (
                                  <FormHelperText className={classes.errorText}>
                                    {errors[`${priceIndex}-${materialIndex}`]}
                                  </FormHelperText>
                                )}
                                <Typography variant="caption" display="block">
                                  Total for two loads: ${(material.loads[1] * 2).toFixed(2)}
                                </Typography>
                              </Grid>
                              <Grid item xs={2}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => removeSecondLoad(priceIndex, materialIndex)}
                                >
                                  Remove Discount
                                </Button>
                              </Grid>
                            </>
                          ) : (
                            <Grid item xs={7}>
                              <Button
                                variant="outlined"
                                onClick={() => addSecondLoad(priceIndex, materialIndex)}
                              >
                                Add Two-Load Discount
                              </Button>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <Button
                              variant={price.materials.length === 1 ? "outlined" : "contained"}
                              color="secondary"
                              size="small"
                              onClick={() => removeMaterial(priceIndex, materialIndex)}
                              disabled={price.materials.length === 1}
                            >
                              Remove Material Group
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => addMaterial(priceIndex)}
                      style={{ marginTop: '8px' }}
                    >
                      Add Material Group
                    </Button>
                  </Paper>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addPrice}
                  style={{ margin: '16px 8px 16px 0' }}
                >
                  Add Price Entry
                </Button>

                <div style={{ marginTop: '16px' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveModel}
                    style={{ marginRight: '8px' }}
                  >
                    Save Model
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Typography variant="body2" color="textSecondary">
                  Prices: {model.prices.length} entries
                </Typography>
                {model.prices.map((price, idx) => (
                  <div key={idx} style={{ marginTop: '8px' }}>
                    <Typography variant="body2">
                      <strong>Effective:</strong> {price.effectiveDate} 
                      {price.city && <span>, <strong>City:</strong> {price.city}</span>}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog open={newModelDialog} onClose={() => setNewModelDialog(false)}>
        <DialogTitle>Add New Pricing Model</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Model Name"
            value={newModelName}
            onChange={(e) => setNewModelName(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewModelDialog(false)}>Cancel</Button>
          <Button onClick={handleAddNewModel} color="primary">Add</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}